import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import { MdClose } from "react-icons/md";
import Lottie from "lottie-react";
import { useState } from "react";

import CryingAnimation from "../../../../assets/animations/CryingAnimation.json";
import TrophyAnimation from "../../../../assets/animations/Trophy.json";
import UpdateInformationAnimation from "../../../../assets/animations/UpdateInformation.json";

import { UseModalPayload } from "../../../../hooks/useModal";
import graphQLClient from "../../../../utils/authHeader";
import REQUEST_CONTRIBUTION_REFUND_GQL from "../../../../utils/mutations/invest/refund-investment";
import useRefundContributionStyles from "./style";

export interface ContributionRefundPayload
  extends Omit<UseModalPayload, "status" | "message"> {
  title: string;
  id: string;
}

type ContributionRefundModalProps = {
  isOpen: boolean;
  payload: ContributionRefundPayload;
  onClose: VoidFunction;
  onSuccess?: (option?: any) => void;
};

type RequestRefund =
  | "WIZARD_STAGE_REQUEST_REFUND"
  | "WIZARD_STAGE_REQUEST_REFUND_RESULT";

const ContributionRefundModal = ({
  isOpen,
  payload,
  onClose,
  onSuccess,
}: ContributionRefundModalProps) => {
  const { title, id } = payload ?? {};
  const classes = useRefundContributionStyles();
  const [result, setResult] = useState<
    | {
        status: "success" | "error";
        message?: string;
      }
    | undefined
  >(undefined);

  const [requestRefundWizardStage, setRequestRefundWizardStage] =
    useState<RequestRefund>("WIZARD_STAGE_REQUEST_REFUND");

  const requestRund = () => {
    graphQLClient
      .request(REQUEST_CONTRIBUTION_REFUND_GQL, { investmentId: id })
      .then((response) => {
        const { requestContributionRefund } = response ?? {};
        setRequestRefundWizardStage("WIZARD_STAGE_REQUEST_REFUND_RESULT");
        onSuccess?.(requestContributionRefund);
        setResult({
          status: "success",
          message: "Refund request successful",
        });
      })
      .catch((error) => {
        const { response } = error;
        const { errors } = response;
        setRequestRefundWizardStage("WIZARD_STAGE_REQUEST_REFUND_RESULT");
        setResult({
          status: "error",
          message: errors?.[0]?.message ?? "Refund request failed",
        });
      });
  };

  const handleDialogClose = () => {
    onClose();
    setTimeout(() => {
      setRequestRefundWizardStage("WIZARD_STAGE_REQUEST_REFUND");
      setResult(undefined);
    }, 500);
  };

  return (
    <>
      <Box component="section">
        <Dialog
          open={isOpen}
          onClose={onClose}
          className={classes.modalpages}
          aria-labelledby="contribution"
          aria-describedby="contribution-result"
          disableEscapeKeyDown={false}
        >
          <DialogTitle>
            <Grid
              container
              justifyContent="flex-end"
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid container item>
                <Typography className={classes.title} color="secondary">
                  Refund Contribution
                </Typography>
              </Grid>
              <Grid item style={{ position: "absolute", right: 5 }}>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="close"
                  style={{ marginRight: 0 }}
                  onClick={handleDialogClose}
                >
                  <MdClose />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <Grid
            container
            style={{
              padding: "20px",
              display: "block",
            }}
            direction="column"
          >
            {requestRefundWizardStage ===
            "WIZARD_STAGE_REQUEST_REFUND_RESULT" ? (
              <RefundResultWizardStage result={result} />
            ) : (
              <RefundInfoWizardStage projectName={title} />
            )}
            <Grid item>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={handleDialogClose}
                >
                  Close
                </Button>
                {requestRefundWizardStage !==
                  "WIZARD_STAGE_REQUEST_REFUND_RESULT" && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={requestRund}
                  >
                    Proceed
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Dialog>
      </Box>
    </>
  );
};

interface RefundInfoWizardStageProps {
  projectName: string;
}
const RefundInfoWizardStage = (props: RefundInfoWizardStageProps) => {
  const { projectName } = props;
  const classes = useRefundContributionStyles();
  return (
    <>
      <Grid
        item
        lg={12}
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Lottie
          animationData={UpdateInformationAnimation}
          style={{
            width: "180px",
            height: "auto",
          }}
        />
      </Grid>
      <Grid item>
        <Grid container justifyContent="center" lg={12}>
          <Grid item lg={12}>
            <Typography variant="body1" className={classes.progressMessage}>
              You are about requesting for{" "}
              <span className={classes.projectName}>{projectName}</span>{" "}
              contribution refund
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

interface RefundResultWizardStageProps {
  result?: {
    status: "success" | "error";
    message?: string;
  };
}
const RefundResultWizardStage = (props: RefundResultWizardStageProps) => {
  const classes = useRefundContributionStyles();
  const { result } = props;
  const { status, message } = result ?? {};
  const animationData =
    status === "success" ? TrophyAnimation : CryingAnimation;
  const dialogTitle = status === "success" ? "Congratulations!" : "Oops!";

  return (
    <>
      <Grid
        item
        lg={12}
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Lottie
          animationData={animationData}
          style={{
            width: "180px",
            height: "auto",
          }}
        />
      </Grid>
      <Grid item>
        <Grid container justifyContent="center" lg={12}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Typography variant="h3" className={classes.progressTitle}>
              {dialogTitle}
            </Typography>
            <Typography variant="body1" className={classes.progressMessage}>
              {message}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ContributionRefundModal;
